import axios from 'axios';
import { getAuth } from 'firebase/auth';


export function getPrevMonthStartDate() {
    const date = new Date();
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return formatDate(firstDayOfMonth);
  }

export function getPrevMonthEndDate() {
    const date = new Date();
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 0);
    return formatDate(lastDayOfMonth);
  }

/* take a date in the format of 2024-01-31 and returns 31 Jan 2024*/
export function formatDateToZoho(dateString) {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const [year, month, day] = dateString.split('-');
    const monthIndex = parseInt(month, 10) - 1; // Month index starts from 0
    
    return `${parseInt(day, 10)} ${months[monthIndex]} ${year}`;
  }

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

/**
 * Returns the given date (or today's date if omitted) formatted as 'DD MMM YYYY'.
 * Optionally allows adding or subtracting days.
 * @param {Date|string} inputDate - The input date (defaults to today if omitted).
 * @param {number} daysToAdd - The number of days to add (can be negative).
 * @returns {string} - Formatted date as 'DD MMM YYYY'.
 */
export const getFormattedDate = (inputDate = new Date(), daysToAdd = 0) => {
  const date = new Date(inputDate); // Use the passed-in date or default to today
  date.setDate(date.getDate() + daysToAdd); // Adjust the date by daysToAdd

  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()]; // Get abbreviated month name
  const year = date.getFullYear();

  return `${day} ${month} ${year}`; // Format as 'DD MMM YYYY'
};

export const getFormattedDateTime = (inputDate = new Date(), daysToAdd = 0) => {
  const date = new Date(inputDate); // Use the passed-in date or the current date

  date.setDate(date.getDate() + daysToAdd); // Adjust the date by daysToAdd

  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()]; // Get abbreviated month name
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`; // Format as 'DD MMM YYYY HH:MM:SS'
};


export const getFormattedISODateTime = (inputDate = new Date()) => {
  console.log('Received Date; ',inputDate);
  const now = new Date(inputDate);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const formatPatientAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return '';

  const now = new Date();
  const ageMs = now.getTime() - dob.getTime();
  if (ageMs < 0) return '';

  const ageInYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
  if (ageInYears >= 1) {
    return `${Math.floor(ageInYears)}yrs`;
  }

  const ageInMonths = Math.floor(ageMs / (30.44 * 24 * 60 * 60 * 1000));
  if (ageInMonths >= 1) {
    return `${ageInMonths}mo`;
  }

  const ageInWeeks = Math.max(1, Math.floor(ageMs / (7 * 24 * 60 * 60 * 1000)));
  return `${ageInWeeks}wk`;
};

// In your component
<input type="datetime-local" defaultValue={getFormattedDateTime()} />

/**
 * Sets up Axios interceptors for handling authorization and error responses globally.
 *
 * Usage:
 * Call this function during application initialization (e.g., in App.js or AuthProvider)
 * to ensure Axios requests are consistently configured with the required headers
 * and error handling logic.
 *
 * Features:
 * - Automatically attaches the Authorization header with a Bearer token from the current user's session.
 * - Refreshes expired tokens by using Firebase's `getIdToken()` method.
 * - Handles 401 (Unauthorized) and 403 (Forbidden) errors, allowing for redirection or other custom behavior.
 *
 * Dependencies:
 * - Axios: Ensure Axios is imported and globally used for API requests.
 * - Firebase Auth: Uses Firebase authentication to retrieve ID tokens.
 *
 * Example:
 * ```
 * import setupAxiosInterceptors from './utils/Utils';
 * setupAxiosInterceptors();
 * ```
 *
 * Notes:
 * - This function modifies Axios' global behavior, so it should be used with caution in multi-instance scenarios.
 * - Ensure Firebase and Axios are properly configured in your project.
 *
 * @returns {void} This function does not return any value.
 */
let interceptorsSetup = false; // Track if interceptors are already set up

const isSessionManagedRequest = (url = '') => {
  return url.includes('/user-activity/active-users') || url.includes('/user-activity/logout');
};

export const setupAxiosInterceptors = () => {
  const auth = getAuth();

  // Only set up interceptors once to avoid multiple registrations
  if (interceptorsSetup) {
    console.log('Interceptors already set up, skipping');
    return;
  }

  // Clear any existing interceptors first
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();

  axios.interceptors.request.use(
    async (config) => {
      const user = auth.currentUser;
      if (user) {
        // Always get the current session ID from localStorage, not from any cache
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
          config.headers['x-session-id'] = sessionId;
        }
        
        // Add device fingerprint if not already present
        if (!config.headers['x-device-fingerprint']) {
          try {
            const fingerprint = await getDeviceFingerprint();
            config.headers['x-device-fingerprint'] = fingerprint;
          } catch (error) {
            console.error('Error getting device fingerprint in interceptor:', error);
          }
        }
        
        // Only add auth header if not already present
        if (!config.headers.Authorization) {
          try {
            // Only get token if absolutely necessary and not for logout endpoints
            if (!config.url.includes('/logout')) {
              const idToken = await user.getIdToken(false); // Never force refresh
              config.headers.Authorization = `Bearer ${idToken}`;
            }
          } catch (error) {
            console.error('Error getting token in interceptor:', error);
          }
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => {
      // Only handle session ID updates, nothing else
      const newSessionId = response.headers['x-new-session-id'];
      if (
        newSessionId &&
        !response.config.url.includes('/logout') &&
        isSessionManagedRequest(response.config.url) &&
        !!auth.currentUser
      ) {
        const currentSessionId = localStorage.getItem('sessionId');
        if (currentSessionId !== newSessionId) {
          console.log('Utils interceptor: Server sent new session ID update');
          localStorage.setItem('sessionId', newSessionId);
          localStorage.setItem('sessionCreatedAt', Date.now().toString());
          
          // Dispatch event to notify useAuth of session change
          window.dispatchEvent(new CustomEvent('sessionIdUpdated', { 
            detail: { sessionId: newSessionId } 
          }));
        } else {
          console.log('Utils interceptor: Session ID unchanged');
        }
      }
      return response;
    },
    (error) => {
      // Handle session ID updates even in error responses
      if (error.response && !error.response.config.url.includes('/logout')) {
        const newSessionId = error.response.headers['x-new-session-id'];
        if (
          newSessionId &&
          isSessionManagedRequest(error.response.config.url) &&
          !!auth.currentUser
        ) {
          const currentSessionId = localStorage.getItem('sessionId');
          if (currentSessionId !== newSessionId) {
            console.log('Utils interceptor: Server sent new session ID update (error response)');
            localStorage.setItem('sessionId', newSessionId);
            localStorage.setItem('sessionCreatedAt', Date.now().toString());
            window.dispatchEvent(new CustomEvent('sessionIdUpdated', { 
              detail: { sessionId: newSessionId } 
            }));
          }
        }

        // SESSION_REVOKED / SESSION_EXPIRED: the server no longer accepts this session.
        // Sign out of Firebase immediately so the user is bounced to the login screen.
        const requestUrl = error.response.config?.url || '';
        const isRestoreProbe = requestUrl.includes('/user-activity/active-users?trigger=restore');
        if (
          error.response.status === 401 &&
          ['SESSION_REVOKED', 'SESSION_EXPIRED'].includes(error.response.data?.error) &&
          !isRestoreProbe
        ) {
          console.warn(`[Security] ${error.response.data?.error} from server - signing out of Firebase`);
          localStorage.removeItem('sessionId');
          localStorage.removeItem('sessionCreatedAt');
          // Use dynamic import to avoid a circular dep with useAuth
          getAuth().signOut().catch(() => {});
          window.dispatchEvent(new CustomEvent('sessionRevoked'));
        }
      }
      
      // Don't try to refresh tokens on 401 - let the app handle it
      console.error('Request failed:', error.response?.status);
      return Promise.reject(error);
    }
  );

  interceptorsSetup = true;
  console.log('Axios interceptors set up successfully');
};



/**
 * Recursively searches for a permission key in a permissions object.
 * @param {Object} permissionsObject - The permissions object.
 * @param {string} key - The permission key to look for.
 * @param {string} type - The permission type (e.g., "View", "Edit").
 * @returns {boolean} - True if the permission is granted.
 */
export const findPermission = (permissionsObject, key, type) => {
  // Direct match
 // console.log('Checking Permissions from Util for key:',key,' and type: ',type);
  if (permissionsObject?.[key]?.[type] === 'true') {
    return true;
  }
  // Check children if they exist
  for (const prop in permissionsObject) {
    if (permissionsObject[prop].children) {
      if (findPermission(permissionsObject[prop].children, key, type)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Checks if the given permissions object grants a specific permission.
 * @param {Object} permissions - The entire permissions object.
 * @param {string} key - The permission key to check.
 * @param {string} type - The permission type ("View", "Add", "Edit", "Delete").
 * @returns {boolean} - True if permission is granted.
 */
export const checkPermission = (permissions, key, type) => {
  return findPermission(permissions, key, type);
};

export const triggerNotification = (message, type = 'info', isCritical = false) => {
  const event = new CustomEvent('newNotification', {
    detail: {
      message,
      type,
      isCritical
    }
  });
  window.dispatchEvent(event);
};

// Example usage:
// triggerNotification('System will restart in 5 minutes', 'warning', true);
// triggerNotification('New message received', 'info', false);

/**
 * Get or generate a persistent device fingerprint (UUID).
 * Stored in localStorage so it survives page reloads and login/logout cycles.
 * Each browser profile on each device gets a truly unique identifier.
 * Unlike the previous hash-based approach (which could collide across devices
 * with the same browser/OS), a random UUID has ~1 in 2^122 collision probability.
 * @returns {Promise<string>} - A UUID v4 string unique to this browser profile
 */
const DEVICE_FINGERPRINT_KEY = 'device_fingerprint';

function generateUUID() {
  // Use crypto.randomUUID if available (modern browsers), otherwise fallback
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const getDeviceFingerprint = async () => {
  let fingerprint = localStorage.getItem(DEVICE_FINGERPRINT_KEY);
  if (!fingerprint) {
    fingerprint = generateUUID();
    localStorage.setItem(DEVICE_FINGERPRINT_KEY, fingerprint);
    console.log('[Fingerprint] Generated new device UUID');
  }
  return fingerprint;
};
